import { LookbookLocationContext } from '@app/logic/lookbook';
import { useLookbookDetails } from '@app/services/hooks/lookbook/useLookbookDetails';
import { DeliveryWindow, Pricelist } from '@typings/@centra';
import React from 'react';

interface Context {
  lookbookId: string;
  pricelists: Pricelist[];
  selectedPricelist: Pricelist | undefined;
  setSelectedPricelistId: (pricelistId: string) => void;
  selectedDeliveryWindows: DeliveryWindow.Mixed[];
  allowAccountRequest: boolean;
  locationContext: LookbookLocationContext;
}

export const useLookbookContext = (): Context => {
  const context = React.useContext(LookbookContext);
  if (context === null) {
    throw new Error('LookbookContext can not be used outside the scope of LookbookContextProvider');
  }

  return context;
};

const LookbookContext = React.createContext<Context | null>(null);
interface LookbookContextProviderProps {
  lookbookId: string;
  locationContext: LookbookLocationContext;
}

export const LookbookContextProvider = ({ children, lookbookId, locationContext }: React.WithChildren<LookbookContextProviderProps>) => {
  const { data: lookbookDetails } = useLookbookDetails({ lookbookId });
  const [selectedPricelistId, setSelectedPricelistId] = React.useState<string | undefined>();

  const pricelists = React.useMemo(() => {
    return lookbookDetails?.pricelists ?? [];
  }, [lookbookDetails]);

  const selectedPricelist = React.useMemo(() => {
    return pricelists.find(pricelist => pricelist.id === selectedPricelistId) ?? pricelists[0];
  }, [pricelists, selectedPricelistId]);

  const selectedDeliveryWindows = React.useMemo(() => {
    return lookbookDetails?.deliveryWindows ?? [];
  }, [lookbookDetails?.deliveryWindows]);
  const allowAccountRequest = lookbookDetails?.allowAccountRequest ?? false;

  return (
    <LookbookContext.Provider
      value={{
        allowAccountRequest,
        locationContext,
        lookbookId,
        pricelists,
        selectedDeliveryWindows,
        selectedPricelist,
        setSelectedPricelistId,
      }}
    >
      {children}
    </LookbookContext.Provider>
  );
};
